import { useEffect, useState, useRef } from 'react';
import { Navigation } from '@/components/Navigation';
import { useAuth } from '@/context/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { formatPrice } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import axios from 'axios';
import { toast } from 'sonner';
import { Package, Plus, Edit, Trash2, Save, X, LayoutDashboard, IndianRupee, Upload, Image, ChevronDown } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Existing categories and subcategories
const EXISTING_CATEGORIES = [
  'Modular Office Workstation',
  'Storage Cabinets',
  'Privacy Booths'
];

const EXISTING_SUBCATEGORIES = {
  'Modular Office Workstation': ['Linear Workstation', 'Cluster of 2', 'Cluster of 4', 'Cluster of 4 Large'],
  'Storage Cabinets': ['Pedestal', 'Filing Cabinet', 'Locker'],
  'Privacy Booths': ['Phone Booth', 'Meeting Booth', 'Meeting Booth Large']
};

export default function AdminProducts() {
  const { user, token, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [showPriceEditor, setShowPriceEditor] = useState(false);
  const [editingPriceProduct, setEditingPriceProduct] = useState(null);
  const [uploadedImages, setUploadedImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  
  // Category/Subcategory mode
  const [useNewCategory, setUseNewCategory] = useState(false);
  const [useNewSubcategory, setUseNewSubcategory] = useState(false);
  
  const [priceFormData, setPriceFormData] = useState({
    mrp: '',
    price: '',
    discount_percent: 0
  });
  
  const [formData, setFormData] = useState({
    name: '',
    category: 'Modular Office Workstation',
    newCategory: '',
    subcategory: '',
    newSubcategory: '',
    mrp: '',
    discount_percent: '',
    price: '', // Actual value (calculated)
    description: '',
    images: [],
    specifications: {
      dimensionColors: '',
      materialConstruction: '',
      featuresQuality: '',
      installationCertification: ''
    }
  });

  useEffect(() => {
    // Wait for auth to finish loading before checking user
    if (authLoading) return;
    
    if (!user) {
      navigate('/login');
      return;
    }
    if (!user.is_admin) {
      navigate('/');
      toast.error('Admin access required');
      return;
    }
    fetchProducts();
  }, [user, authLoading, navigate]);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API_URL}/products`);
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSpecificationChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      specifications: {
        ...prev.specifications,
        [field]: value
      }
    }));
  };

  // Calculate actual price when MRP or discount changes
  const handlePricingChange = (field, value) => {
    const newFormData = { ...formData, [field]: value };
    
    if (field === 'mrp' || field === 'discount_percent') {
      const mrp = parseFloat(field === 'mrp' ? value : newFormData.mrp) || 0;
      const discount = parseFloat(field === 'discount_percent' ? value : newFormData.discount_percent) || 0;
      
      if (mrp > 0 && discount >= 0) {
        const actualPrice = Math.round(mrp - (mrp * discount / 100));
        newFormData.price = actualPrice.toString();
      }
    }
    
    setFormData(newFormData);
  };

  // Handle image upload
  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    setUploading(true);
    const newImages = [];
    
    for (const file of files) {
      try {
        const formDataUpload = new FormData();
        formDataUpload.append('file', file);
        
        const response = await axios.post(`${API_URL}/upload/image`, formDataUpload, {
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: `Bearer ${token}`
          }
        });
        
        newImages.push(response.data.url);
      } catch (error) {
        console.error('Error uploading image:', error);
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    
    setUploadedImages(prev => [...prev, ...newImages]);
    setFormData(prev => ({
      ...prev,
      images: [...prev.images, ...newImages]
    }));
    setUploading(false);
    
    if (newImages.length > 0) {
      toast.success(`${newImages.length} image(s) uploaded successfully`);
    }
  };

  const removeImage = (index) => {
    setUploadedImages(prev => prev.filter((_, i) => i !== index));
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const category = useNewCategory ? formData.newCategory : formData.category;
      const subcategory = useNewSubcategory ? formData.newSubcategory : formData.subcategory;
      
      if (!category) {
        toast.error('Please select or enter a category');
        return;
      }
      
      const productData = {
        name: formData.name,
        category: category,
        subcategory: subcategory,
        price: parseFloat(formData.price) || 0,
        mrp: parseFloat(formData.mrp) || 0,
        discount_percent: parseFloat(formData.discount_percent) || 0,
        description: formData.description,
        image_url: formData.images[0] || '', // Primary image
        images: formData.images, // All images
        specifications: formData.specifications
      };

      if (editingProduct) {
        await axios.put(`${API_URL}/admin/products/${editingProduct.id}`, productData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Product updated successfully');
      } else {
        await axios.post(`${API_URL}/admin/products`, productData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Product created successfully');
      }
      
      resetForm();
      fetchProducts();
    } catch (error) {
      console.error('Error saving product:', error);
      toast.error(error.response?.data?.detail || 'Failed to save product');
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      category: product.category,
      newCategory: '',
      subcategory: product.subcategory || '',
      newSubcategory: '',
      mrp: product.mrp?.toString() || '',
      discount_percent: product.discount_percent?.toString() || '',
      price: product.price?.toString() || '',
      description: product.description || '',
      images: product.images || [product.image_url].filter(Boolean),
      specifications: product.specifications || {
        dimensionColors: '',
        materialConstruction: '',
        featuresQuality: '',
        installationCertification: ''
      }
    });
    setUploadedImages(product.images || [product.image_url].filter(Boolean));
    setUseNewCategory(false);
    setUseNewSubcategory(false);
    setShowForm(true);
  };

  // Price Editor Functions
  const handleOpenPriceEditor = (product) => {
    setEditingPriceProduct(product);
    setPriceFormData({
      mrp: product.mrp?.toString() || '',
      price: product.price?.toString() || '',
      discount_percent: product.discount_percent || 0
    });
    setShowPriceEditor(true);
  };

  const handlePriceInputChange = (field, value) => {
    const newData = { ...priceFormData, [field]: value };
    
    if (field === 'mrp' || field === 'price') {
      const mrp = parseFloat(field === 'mrp' ? value : newData.mrp) || 0;
      const price = parseFloat(field === 'price' ? value : newData.price) || 0;
      
      if (mrp > 0 && price > 0 && mrp > price) {
        const discount = Math.round(((mrp - price) / mrp) * 100);
        newData.discount_percent = discount;
      } else {
        newData.discount_percent = 0;
      }
    }
    
    setPriceFormData(newData);
  };

  const handleSavePrice = async () => {
    if (!editingPriceProduct) return;
    
    try {
      const mrp = parseFloat(priceFormData.mrp) || 0;
      const price = parseFloat(priceFormData.price) || 0;
      
      if (mrp <= 0 || price <= 0) {
        toast.error('Please enter valid MRP and Price values');
        return;
      }
      
      if (price > mrp) {
        toast.error('Discounted price cannot be greater than MRP');
        return;
      }

      await axios.patch(
        `${API_URL}/admin/products/${editingPriceProduct.id}/pricing`,
        {
          mrp: mrp,
          price: price,
          discount_percent: priceFormData.discount_percent
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Pricing updated successfully');
      setShowPriceEditor(false);
      setEditingPriceProduct(null);
      fetchProducts();
    } catch (error) {
      console.error('Error updating pricing:', error);
      toast.error(error.response?.data?.detail || 'Failed to update pricing');
    }
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/admin/products/${productId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Product deleted successfully');
      fetchProducts();
    } catch (error) {
      console.error('Error deleting product:', error);
      toast.error('Failed to delete product');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      category: 'Modular Office Workstation',
      newCategory: '',
      subcategory: '',
      newSubcategory: '',
      mrp: '',
      discount_percent: '',
      price: '',
      description: '',
      images: [],
      specifications: {
        dimensionColors: '',
        materialConstruction: '',
        featuresQuality: '',
        installationCertification: ''
      }
    });
    setUploadedImages([]);
    setEditingProduct(null);
    setShowForm(false);
    setUseNewCategory(false);
    setUseNewSubcategory(false);
  };

  // Show loading while auth is being verified
  if (authLoading) {
    return (
      <div className="min-h-screen noise-texture flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground uppercase tracking-wider">Verifying access...</p>
        </div>
      </div>
    );
  }

  // Don't render if not authenticated as admin
  if (!user || !user.is_admin) {
    return null;
  }

  if (loading) {
    return (
      <div className="min-h-screen noise-texture">
        <Navigation />
        <div className="container mx-auto px-8 py-20 text-center">
          <p className="text-xl">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen noise-texture">
      <Navigation />

      <div className="container mx-auto px-8 py-12">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl md:text-5xl font-black uppercase mb-2">Product Management</h1>
            <p className="text-muted-foreground">Manage your product catalog</p>
          </div>
          <div className="flex gap-4">
            <Link to="/admin/dashboard">
              <Button variant="outline" className="rounded-none uppercase tracking-wider font-bold">
                <LayoutDashboard className="w-4 h-4 mr-2" />
                Dashboard
              </Button>
            </Link>
            <Button
              onClick={() => setShowForm(!showForm)}
              className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-none uppercase tracking-wider font-bold"
            >
              {showForm ? (
                <>
                  <X className="w-4 h-4 mr-2" />
                  Cancel
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Product
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Product Form */}
        {showForm && (
          <div className="bg-white border-2 border-primary rounded-none p-8 mb-8">
            <h2 className="text-2xl font-black uppercase mb-6">
              {editingProduct ? 'Edit Product' : 'Add New Product'}
            </h2>
            
            <form onSubmit={handleSubmit} className="space-y-8">
              {/* 1. Product Name */}
              <div>
                <Label className="uppercase tracking-wider text-xs font-bold">1. Product Name *</Label>
                <Input
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="rounded-none mt-2"
                  placeholder="Enter product name"
                />
              </div>

              {/* 2. Category Selection */}
              <div className="bg-gray-50 p-6 rounded-none border">
                <Label className="uppercase tracking-wider text-xs font-bold mb-4 block">2. Category *</Label>
                
                <div className="flex items-center gap-4 mb-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      checked={!useNewCategory}
                      onChange={() => setUseNewCategory(false)}
                      className="w-4 h-4"
                    />
                    <span className="text-sm font-medium">Add in Existing Category</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      checked={useNewCategory}
                      onChange={() => setUseNewCategory(true)}
                      className="w-4 h-4"
                    />
                    <span className="text-sm font-medium">Add New Category</span>
                  </label>
                </div>

                {useNewCategory ? (
                  <Input
                    name="newCategory"
                    value={formData.newCategory}
                    onChange={handleInputChange}
                    className="rounded-none"
                    placeholder="Enter new category name"
                  />
                ) : (
                  <select
                    name="category"
                    value={formData.category}
                    onChange={handleInputChange}
                    className="w-full border border-input px-3 py-2 rounded-none bg-white"
                  >
                    {EXISTING_CATEGORIES.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                )}
              </div>

              {/* 3. Sub-Category Selection */}
              <div className="bg-gray-50 p-6 rounded-none border">
                <Label className="uppercase tracking-wider text-xs font-bold mb-4 block">3. Sub-Category</Label>
                
                <div className="flex items-center gap-4 mb-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      checked={!useNewSubcategory}
                      onChange={() => setUseNewSubcategory(false)}
                      className="w-4 h-4"
                    />
                    <span className="text-sm font-medium">Add in Existing Sub-Category</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      checked={useNewSubcategory}
                      onChange={() => setUseNewSubcategory(true)}
                      className="w-4 h-4"
                    />
                    <span className="text-sm font-medium">Add New Sub-Category</span>
                  </label>
                </div>

                {useNewSubcategory ? (
                  <Input
                    name="newSubcategory"
                    value={formData.newSubcategory}
                    onChange={handleInputChange}
                    className="rounded-none"
                    placeholder="Enter new sub-category name"
                  />
                ) : (
                  <select
                    name="subcategory"
                    value={formData.subcategory}
                    onChange={handleInputChange}
                    className="w-full border border-input px-3 py-2 rounded-none bg-white"
                  >
                    <option value="">Select Sub-Category</option>
                    {(EXISTING_SUBCATEGORIES[formData.category] || []).map(subcat => (
                      <option key={subcat} value={subcat}>{subcat}</option>
                    ))}
                  </select>
                )}
              </div>

              {/* 4. Pricing Section */}
              <div className="bg-green-50 p-6 rounded-none border border-green-200">
                <Label className="uppercase tracking-wider text-xs font-bold mb-4 block text-green-800">4. Pricing</Label>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <Label className="text-xs font-bold text-gray-600">MRP (₹) *</Label>
                    <Input
                      type="number"
                      name="mrp"
                      value={formData.mrp}
                      onChange={(e) => handlePricingChange('mrp', e.target.value)}
                      required
                      className="rounded-none mt-1"
                      placeholder="Enter MRP"
                    />
                  </div>
                  
                  <div>
                    <Label className="text-xs font-bold text-gray-600">Discount % *</Label>
                    <Input
                      type="number"
                      name="discount_percent"
                      value={formData.discount_percent}
                      onChange={(e) => handlePricingChange('discount_percent', e.target.value)}
                      required
                      min="0"
                      max="100"
                      className="rounded-none mt-1"
                      placeholder="Enter discount %"
                    />
                  </div>
                  
                  <div>
                    <Label className="text-xs font-bold text-gray-600">Actual Value (₹)</Label>
                    <div className="bg-white border border-input px-3 py-2 rounded-none mt-1 font-bold text-lg text-green-700">
                      {formData.price ? `₹${parseInt(formData.price).toLocaleString('en-IN')}` : '₹0'}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Auto-calculated</p>
                  </div>
                </div>
              </div>

              {/* 5. Product Images */}
              <div className="bg-blue-50 p-6 rounded-none border border-blue-200">
                <Label className="uppercase tracking-wider text-xs font-bold mb-4 block text-blue-800">5. Product Images</Label>
                
                <div 
                  onClick={() => fileInputRef.current?.click()}
                  className="border-2 border-dashed border-blue-300 rounded-lg p-8 text-center cursor-pointer hover:border-blue-500 transition-colors"
                >
                  <Upload className="w-12 h-12 mx-auto mb-4 text-blue-400" />
                  <p className="text-sm font-medium text-blue-700">Click to browse or drag & drop images</p>
                  <p className="text-xs text-blue-500 mt-1">Supports: JPG, PNG, WebP (Multiple files allowed)</p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleImageUpload}
                    className="hidden"
                  />
                </div>
                
                {uploading && (
                  <p className="text-center text-blue-600 mt-4">Uploading images...</p>
                )}
                
                {/* Uploaded Images Preview */}
                {uploadedImages.length > 0 && (
                  <div className="mt-4">
                    <p className="text-sm font-bold mb-2">Uploaded Images ({uploadedImages.length})</p>
                    <div className="flex flex-wrap gap-4">
                      {uploadedImages.map((url, index) => (
                        <div key={index} className="relative group">
                          <img
                            src={url}
                            alt={`Product ${index + 1}`}
                            className="w-24 h-24 object-cover border-2 border-gray-200"
                          />
                          {index === 0 && (
                            <span className="absolute top-0 left-0 bg-green-500 text-white text-xs px-1">Primary</span>
                          )}
                          <button
                            type="button"
                            onClick={() => removeImage(index)}
                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* 6. Product Description */}
              <div>
                <Label className="uppercase tracking-wider text-xs font-bold">6. Product Description</Label>
                <Textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  className="rounded-none mt-2 min-h-[100px]"
                  placeholder="Enter product description"
                />
              </div>

              {/* 7. Product Specifications */}
              <div className="bg-purple-50 p-6 rounded-none border border-purple-200">
                <Label className="uppercase tracking-wider text-xs font-bold mb-4 block text-purple-800">7. Product Specifications</Label>
                
                <div className="space-y-4">
                  <div>
                    <Label className="text-xs font-bold text-gray-600">Dimension & Colors</Label>
                    <Textarea
                      value={formData.specifications.dimensionColors}
                      onChange={(e) => handleSpecificationChange('dimensionColors', e.target.value)}
                      className="rounded-none mt-1 min-h-[80px]"
                      placeholder="e.g., Length: 120cm, Width: 60cm, Height: 75cm&#10;Available Colors: White, Oak, Walnut"
                    />
                  </div>
                  
                  <div>
                    <Label className="text-xs font-bold text-gray-600">Material & Construction</Label>
                    <Textarea
                      value={formData.specifications.materialConstruction}
                      onChange={(e) => handleSpecificationChange('materialConstruction', e.target.value)}
                      className="rounded-none mt-1 min-h-[80px]"
                      placeholder="e.g., Frame: Powder-coated steel&#10;Top: High-pressure laminate&#10;Edge: ABS edge banding"
                    />
                  </div>
                  
                  <div>
                    <Label className="text-xs font-bold text-gray-600">Features & Quality</Label>
                    <Textarea
                      value={formData.specifications.featuresQuality}
                      onChange={(e) => handleSpecificationChange('featuresQuality', e.target.value)}
                      className="rounded-none mt-1 min-h-[80px]"
                      placeholder="e.g., Cable management system&#10;Height adjustable feet&#10;Scratch-resistant surface"
                    />
                  </div>
                  
                  <div>
                    <Label className="text-xs font-bold text-gray-600">Installation & Certification</Label>
                    <Textarea
                      value={formData.specifications.installationCertification}
                      onChange={(e) => handleSpecificationChange('installationCertification', e.target.value)}
                      className="rounded-none mt-1 min-h-[80px]"
                      placeholder="e.g., Easy DIY assembly&#10;Installation manual included&#10;ISO 9001 certified manufacturing"
                    />
                  </div>
                </div>
              </div>

              {/* Submit Buttons */}
              <div className="flex gap-4 pt-4">
                <Button
                  type="submit"
                  className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90 rounded-none uppercase tracking-wider font-bold py-4"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {editingProduct ? 'Update Product' : 'Create Product'}
                </Button>
                <Button
                  type="button"
                  onClick={resetForm}
                  variant="outline"
                  className="rounded-none uppercase tracking-wider font-bold"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        )}

        {/* Price Editor Modal */}
        {showPriceEditor && editingPriceProduct && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white border-2 border-primary rounded-none p-8 max-w-md w-full mx-4">
              <h2 className="text-2xl font-black uppercase mb-2">Edit Pricing</h2>
              <p className="text-muted-foreground mb-6">{editingPriceProduct.name}</p>
              
              <div className="space-y-4">
                <div>
                  <Label className="uppercase tracking-wider text-xs font-bold">MRP (₹)</Label>
                  <Input
                    type="number"
                    value={priceFormData.mrp}
                    onChange={(e) => handlePriceInputChange('mrp', e.target.value)}
                    className="rounded-none text-lg"
                    placeholder="Enter MRP"
                  />
                </div>
                
                <div>
                  <Label className="uppercase tracking-wider text-xs font-bold">Discounted Price (₹)</Label>
                  <Input
                    type="number"
                    value={priceFormData.price}
                    onChange={(e) => handlePriceInputChange('price', e.target.value)}
                    className="rounded-none text-lg"
                    placeholder="Enter discounted price"
                  />
                </div>
                
                <div className="bg-accent p-4 border-2 border-accent">
                  <Label className="uppercase tracking-wider text-xs font-bold">Discount Percentage</Label>
                  <div className="flex items-center gap-2 mt-2">
                    <span className="text-3xl font-black text-primary">{priceFormData.discount_percent}%</span>
                    <span className="text-muted-foreground uppercase text-sm">OFF</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">Auto-calculated from MRP & Discounted Price</p>
                </div>
              </div>
              
              <div className="flex gap-4 mt-6">
                <Button
                  onClick={handleSavePrice}
                  className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90 rounded-none uppercase tracking-wider font-bold"
                >
                  <Save className="w-4 h-4 mr-2" />
                  Save Pricing
                </Button>
                <Button
                  onClick={() => {
                    setShowPriceEditor(false);
                    setEditingPriceProduct(null);
                  }}
                  variant="outline"
                  className="rounded-none uppercase tracking-wider font-bold"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Products List */}
        <div className="grid grid-cols-1 gap-6">
          <div className="bg-white border-2 border-border rounded-none p-6">
            <h2 className="text-2xl font-black uppercase mb-6">All Products ({products.length})</h2>
            
            {products.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">No products found. Add your first product!</p>
            ) : (
              <div className="space-y-4">
                {products.map((product) => (
                  <div
                    key={product.id}
                    className="border border-border p-4 flex items-center gap-4 hover:border-primary transition-colors"
                  >
                    <img
                      src={product.image_url}
                      alt={product.name}
                      className="w-24 h-24 object-cover"
                    />
                    <div className="flex-1">
                      <h3 className="font-bold text-lg">{product.name}</h3>
                      <p className="text-sm text-muted-foreground">{product.category} {product.subcategory ? `• ${product.subcategory}` : ''}</p>
                      <div className="flex items-center gap-3 mt-1">
                        {product.mrp && (
                          <span className="text-sm text-muted-foreground line-through">
                            MRP {formatPrice(product.mrp)}
                          </span>
                        )}
                        <span className="text-primary font-bold">{formatPrice(product.price)}</span>
                        {product.discount_percent > 0 && (
                          <span className="bg-green-100 text-green-800 px-2 py-0.5 text-xs font-bold">
                            {product.discount_percent}% OFF
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        onClick={() => handleOpenPriceEditor(product)}
                        variant="outline"
                        size="sm"
                        className="rounded-none bg-green-50 hover:bg-green-100 border-green-300"
                        title="Edit Pricing"
                      >
                        <IndianRupee className="w-4 h-4 text-green-700" />
                      </Button>
                      <Button
                        onClick={() => handleEdit(product)}
                        variant="outline"
                        size="sm"
                        className="rounded-none"
                        title="Edit Product"
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        onClick={() => handleDelete(product.id)}
                        variant="destructive"
                        size="sm"
                        className="rounded-none"
                        title="Delete Product"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
